import * as STD from '@emersya/mpa-std-lib';

import CM                                from '@emersya/mpa-components-manager-lib';
import {
	Input,
	InputNumber,
	InputBool,
	InputImage
}                                        from '@emersya/mpa-components-lib';

export interface IViewerGraphicsValue {
	enabled : boolean;
	leftRight : number;
	showEditor : boolean;
	upDown : number;
	resize : number;
	rotate : number;
	image : HTMLImageElement | null;
};

export interface IViewerGraphicsValueWithImage extends IViewerGraphicsValue {
	enabled : true;
	showEditor : boolean;
	leftRight : number;
	upDown : number;
	resize : number;
	rotate : number;
	image : HTMLImageElement;
	viewerInfos : ViewerComponent.IExtended_transformMeshInstanceTextureHelper_response;
};

export interface IViewerGraphicsValueWithoutImage extends IViewerGraphicsValue {
	enabled : boolean;
	showEditor : boolean;
	leftRight : number;
	upDown : number;
	resize : number;
	rotate : number;
	image : null;
};
export type TViewerGraphicsValue = IViewerGraphicsValueWithImage | IViewerGraphicsValueWithoutImage;

export interface IViewerGraphicsValueSerialized {
	enabled : boolean;
	showEditor : boolean;
	leftRight : number;
	upDown : number;
	resize : number;
	rotate : number;
	image : string | null;
};

export interface IViewerGraphicsComponentConfiguration extends Input.Configuration<
	TViewerGraphicsValue,
	IViewerGraphicsValueSerialized
>
{
	viewer : CM.Path.TPathRaw;
	productNode : {
		getProductInstance : CM.PropertyOperation.TDescriptor;
		getSizeX : CM.PropertyOperation.TDescriptor;
		getSizeY : CM.PropertyOperation.TDescriptor;
		getNodeLocalId : CM.PropertyOperation.TDescriptor;
		mesh? : string;
		getMesh? : CM.PropertyOperation.TDescriptor;
		getMaterial? : CM.PropertyOperation.TDescriptor;
		material? : {
			name : string;
			configuration : {
				enabled : string;
				disabled : string;
			};
		};
	};
	forceBackgroundColor? : string | null;
	// basketInfos? : {
	// 	product : string;
	// 	part : string[];
	// 	id : string;
	// 	useSurfaceAsVolume : boolean;
	// 	price : Emersya.Basket.IPrice;
	// 	taxes : Emersya.Basket.ITaxSerialized[];
	// 	/**
	// 	 * Image-*: compute volume from the physical image size
	// 	 * Patch-*: compute volume from the physical full patch size
	// 	 * Default is 'image-exact'
	// 	 **/
	// 	priceComputationByVolumeMethod? : (
	// 		'image-floor' | 'image-round' | 'image-ceil' | 'image-exact' |
	// 		'patch-floor' | 'patch-round' | 'patch-ceil' | 'patch-exact'
	// 		);
	// };
	transformMeshOnConfigurationChange? : boolean;
	isPatchNeedingToBeRestoredToBlankOnDisable? : boolean;
};

export class ViewerGraphicsComponent extends Input.Component<
	HTMLDivElement,
	TViewerGraphicsValue,
	IViewerGraphicsValueSerialized,
	ViewerGraphicsComponent,
	IViewerGraphicsComponentConfiguration
>
{
	static getDefaultValue() : IViewerGraphicsValueWithoutImage {
		return (<any> {
			enabled :                 false,
			showEditor :              true,
			leftRight :               0,
			upDown :                  0,
			resize :                  1,
			rotate :                  0,
			image :                   null,
			defaultCustomImageValue : () => {
			}
		});
	};

	static async deserialize(serialized : IViewerGraphicsValueSerialized) : Promise<IViewerGraphicsValue> {
		return {
			...serialized,
			image : serialized.image ? await STD.DOM.createImageFrom(serialized.image) : null
		};
	};

	viewer! : ViewerComponent; // finalize
	component_DOM : HTMLDivElement;
	getNodeLocalId : CM.PropertyOperation.TOperation;
	getSizeX : CM.PropertyOperation.TOperation;
	getSizeY : CM.PropertyOperation.TOperation;
	getProductInstance : CM.PropertyOperation.TOperation;
	getMeshVG? : CM.PropertyOperation.TOperation;
	getMaterialVG? : CM.PropertyOperation.TOperation;

	get productInstance() : ViewerComponent.ProductInstance | null {
		const instance = this.getProductInstance.execute(this, this.component_getEventWhichRepresentNoEvent());
		if (!instance) {
			return null;
		}
		if (!(instance instanceof ViewerComponent.ProductInstance)) {
			this.component_throwUserError('Instance is not a ProductInstance!', {
				instance
			});
		}
		return instance;
	};

	override async component_finalize() {
		const viewer = CM.root.component_find(this.component_configuration.viewer);
		if (!viewer) {
			this.component_throwUserError('Viewer not found!', {
				viewerPath : this.component_configuration.viewer
			});
		}
		if (!(viewer instanceof ViewerComponent)) {
			this.component_throwUserError('Viewer not is not a Viewer component!', {
				viewerPath : this.component_configuration.viewer,
				viewer
			});
		}
		this.viewer = viewer;
		if (!this.component_configuration.defaultValue && !app.isReloading) {
			await this.updateValue(ViewerGraphicsComponent.getDefaultValue());
		}
		await super.component_finalize();
	};

	override async updateValue(
		newValue : TViewerGraphicsValue,
		_defaultValueMarker? : Symbol
	)
	{
		void newValue;
		void _defaultValueMarker;
		const oldValue = this.value;
		await super.updateValue(newValue, _defaultValueMarker);
		if (
			(_defaultValueMarker && Input.Component.isDefaultValueMarker(_defaultValueMarker)) ||
			!this.component_isStarted /*||
			(<any> window).app.isReloading*/
		)
		{
			return;
		}
		if (this.value !== newValue) {
			this.component_throwLibraryError('');
		}
		const graphicsActivation = <InputBool.Component> this.component_find('@:header.enable');
		if (!oldValue || (oldValue.enabled !== this.value.enabled) || (graphicsActivation.value !== this.value.enabled)) {
			await graphicsActivation.updateValue(this.value.enabled);
		}
		if (!oldValue || (oldValue.leftRight !== this.value.leftRight)) {
			const leftRightRange = <InputNumber.Component> this.component_find('@:editor.move-leftRight.input');
			await leftRightRange.updateValue(this.value.leftRight);
		}
		if (!oldValue || (oldValue.upDown !== this.value.upDown)) {
			const upDownRange = <InputNumber.Component> this.component_find('@:editor.move-upDown.input');
			await upDownRange.updateValue(this.value.upDown);
		}
		if (!oldValue || (oldValue.resize !== this.value.resize)) {
			const resizeRange = <InputNumber.Component> this.component_find('@:editor.resize.input');
			await resizeRange.updateValue(this.value.resize);
		}
		if (!oldValue || (oldValue.rotate !== this.value.rotate)) {
			const rotateRange = <InputNumber.Component> this.component_find('@:editor.rotate.input');
			await rotateRange.updateValue(this.value.rotate);
		}
		if (!oldValue || (oldValue.image !== this.value.image)) {
			const inputImage = <InputImage.Component> this.component_find('@:editor.image');
			if (!(this.value.image instanceof Image)) {
				this.value.image = await inputImage.deserialize(this.value.image);
			}
			await inputImage.updateValue(this.value.image);
		}
		await super.updateValue(newValue, _defaultValueMarker);
	};

	override isValueTheDefault() {
		if (!this.value) {
			this.component_throwLibraryError('');
		}
		if (this.defaultValue) {
			return (STD.object.hasSameContentDeeply(this.value, this.defaultValue));
		}
		type TAnyInput = Input.Component<any, any, any, any, any>;
		const IVTD = (path : string) => (<TAnyInput> this.component_find(path)).isValueTheDefault();
		return (
			IVTD('@:header.enable') &&
			IVTD('@:editor.move-leftRight.input') &&
			IVTD('@:editor.move-upDown.input') &&
			IVTD('@:editor.resize.input') &&
			IVTD('@:editor.rotate.input') &&
			IVTD('@:editor.image')
		);
	};

	override deserialize(x : IViewerGraphicsValueSerialized) {
		return (<TViewerGraphicsValue> <any> x);
	};

	async onInternalValueUpdated(ev : STD.events.Event<any, CM.Component<any, any>>) {
		ev.stopPropagation();
		// if ((<any> window).app.isReloading) {
		// 	return;
		// }
		// if (!this.viewer.viewer.isLoaded()) {
		// 	await this.viewer.viewer.awaitLoading();
		// }
		const v = <typeof this.value> this.value;
		if (!v) {
			return;
		}
		const c = <Input.Component<any, any, any, any, any>> ev.target;
		if (!c.component_parent) {
			throw STD.errors.library('');
		}
		const cid = (c.id === 'input') ? c.component_parent.id : c.id;
		const id = (
			(cid === 'enable') ? 'enabled' :
				(cid === 'image') ? 'image' :
					(cid === 'move-leftRight') ? 'leftRight' :
						(cid === 'move-upDown') ? 'upDown' :
							(cid === 'resize') ? 'resize' :
								(cid === 'rotate') ? 'rotate' :
									''
		);
		if (id === '') {
			throw STD.errors.library('');
		}
		const value = (c.value instanceof Array) ? c.value[ 0 ] : c.value;
		const previouslyEnabled = v.enabled;
		(<any> v)[ id ] = value;
		if (previouslyEnabled || ((id === 'enabled') && value)) {
			const localId = await this.getNodeLocalId.execute(this, this.component_getEventWhichRepresentNoEvent());
			if (typeof localId !== 'number') {
				throw STD.errors.user('getNodeLocalId not returned a number!', {
					localId,
					component : this
				});
			}
			// 	await this.viewer.viewer.freezeRenderingCumulative();
			if (id === 'enabled') {
				await this.handle_activation_change(ev, value, localId);
			} else {
				if (this.value && this.value.enabled) {
					if (id === 'image') {
						await this.handle_image_change(ev, value);
					} else {
						await this.handle_configuration_change(ev, this.value);
					}
				}
			}
			// 	await this.viewer.viewer.unfreezeRenderingCumulative();
		}
		const productInstance = this.productInstance;
		if (productInstance && productInstance.basketPart) {
			const option = productInstance.basketPart.currentOption;
			if (option) {
				const CI = Array.from(option.customImages)[ 0 ];
				if (CI) {
					await option.dispatchCustomOptionUpdate(CI);
				} else {
					await option.dispatchCustomOptionUpdate(null);
				}
			}
		}
		await super.updateValue(this.value);
	};

	async getMaterial() : Promise<NonNullable<IViewerGraphicsComponentConfiguration['productNode']['material']>> {
		if (this.getMaterialVG) {
			return (await this.getMaterialVG.execute(this, this.component_getEventWhichRepresentNoEvent()));
		}
		if (!this.component_configuration.productNode.material) {
			this.component_throwUserError('Material not defined!');
		}
		return (this.component_configuration.productNode.material);
	};

	private async getMesh() : Promise<string> {
		if (this.getMeshVG) {
			return (await this.getMeshVG.execute(this, this.component_getEventWhichRepresentNoEvent()));
		}
		if (!this.component_configuration.productNode.mesh) {
			this.component_throwUserError('Mesh not defined!');
		}
		return (this.component_configuration.productNode.mesh);
	};

	private async handle_activation_change(
		event : STD.events.Event<any, CM.Component<any, any>>,
		value : boolean,
		localId : number
		// ,
		// itShouldBypassBasketUpdateIfItsARemove : boolean = false
	)
	{
		if (app.isReloading) {
			return;
		}
		const nodeLocalId = await this.getNodeLocalId.execute(this, this.component_getEventWhichRepresentNoEvent());
		if (nodeLocalId < 0) {
			return;
		}
		await (<any> window).app.transaction(async () => {
			const material = await this.getMaterial();
			const variation = material.configuration[ value ? 'enabled' : 'disabled' ];
			await this.viewer.setMaterials({
				nodeLocalIds : [ localId ],
				materials :    [
					{
						configurableMaterial : material.name,
						materialVariation :    variation
					}
				]
			});
			if (this.value && this.value.enabled) {
				await this.handle_image_change(event, this.value.image);
			} else {
				///KO
				// await this.viewer.freezeRenderingCumulative();
				// if (this.component_configuration.basketInfos && !itShouldBypassBasketUpdateIfItsARemove) {
				// 	const B = <Emersya.Basket.Basket> (<any> window).app.application.basket;
				// 	const part = B.getPart(
				// 		this.component_configuration.basketInfos.product,
				// 		this.component_configuration.basketInfos.part
				// 	);
				// 	if (
				// 		part.currentOption &&
				// 		part.currentOption.hasCustom('image', this.component_configuration.basketInfos.id)
				// 	)
				// 	{
				// 		await part.currentOption.removeCustom(
				// 			'image',
				// 			this.component_configuration.basketInfos.id
				// 		);
				// 	}
				// }
				// await this.viewer.unfreezeRenderingCumulative();
			}
		});
	};

	private async viewerApplyImage(value : HTMLImageElement | null) {
		if (app.isReloading) {
			return;
		}
		const b64 = value ? value.src : STD.colors.transparentBase64PNGPixel;
		const nodeLocalId = await this.getNodeLocalId.execute(this, this.component_getEventWhichRepresentNoEvent());
		if (nodeLocalId < 0 || app.isReloading) {
			return;
		}
		await this.viewer.updateMeshInstanceCustomTextures([
			{
				name :        await this.getMesh(),
				nodeLocalId : nodeLocalId,
				image :       b64,
				contents :    [
					'albedo',
					'opacity'
				]
			}
		]);
	};

	private async handle_image_change(
		event : STD.events.Event<any, CM.Component<any, any>>,
		value : HTMLImageElement | null
	)
	{
		await this.viewerApplyImage(value);
		if (this.value && this.value.enabled) {
			await this.handle_configuration_change(event, this.value);
		} else {
			// if (this.component_configuration.basketInfos) {
			// 	const B = <Emersya.Basket.Basket> (<any> window).app.application.basket;
			// 	await B.removeCustom(
			// 		this.component_configuration.basketInfos.product,
			// 		this.component_configuration.basketInfos.part,
			// 		'image',
			// 		this.component_configuration.basketInfos.id
			// 	);
			// }
		}
	};

	async refreshViewerInfosAndBasket() {
		if (app.isReloading) {
			return;
		}
		const value = this.value;
		const B = (<MPAEngineApplicationComponent<any, any>> (<any> window).app).basket;
		if (!B) {
			this.component_throwLibraryError('');
		}
		// const B = <Emersya.Basket.Basket> (<any> window).app.application.basket;
		if (!value || !value.image || !value.enabled || this.component_isDisabled) {
			// if (this.component_configuration.basketInfos) {
			// 	// await B.removeCustomIfUsed(
			// 	// 	this.component_configuration.basketInfos.product,
			// 	// 	this.component_configuration.basketInfos.part,
			// 	// 	'image',
			// 	// 	this.component_configuration.basketInfos.id,
			// 	// 	false
			// 	// );
			// }
		} else {
			const v = <IViewerGraphicsValueWithImage> value;
			const mesh = await this.getMesh();
			const patchSizeX = await this.getSizeX.execute(this, this.component_getEventWhichRepresentNoEvent());
			const patchSizeY = await this.getSizeY.execute(this, this.component_getEventWhichRepresentNoEvent());
			v.viewerInfos = this.viewer.transformMeshInstanceTexture_helper(
				{
					image : {
						resolutionX : value.image.naturalWidth,
						resolutionY : value.image.naturalHeight,
						left :        value.leftRight,
						up :          value.upDown,
						scale :       value.resize,
						rotation :    STD.math.degToRad(value.rotate)
					},
					zone :  {
						mesh,
						width :       patchSizeX,
						height :      patchSizeY,
						nodeLocalId : await this.getNodeLocalId.execute(this, this.component_getEventWhichRepresentNoEvent())
					}
				}
			);
			// if (this.component_configuration.basketInfos) {
			// 	const material = await this.getMaterial();
			// 	const [ volume, priceComputationByVolumeMethod ] = (() : [ number, NonNullable<Emersya.Basket.IOptionCustomization<any>['priceComputationByVolumeMethod']> ] => {
			// 		const PCBVM = this.component_configuration.basketInfos.priceComputationByVolumeMethod || 'image-exact';
			// 		const finalMethod = <Emersya.Basket.IOptionCustomization<any>['priceComputationByVolumeMethod']> <any> PCBVM.split(
			// 			'-').slice(-1);
			// 		if (!finalMethod) {
			// 			throw new Error();
			// 		}
			// 		if (PCBVM.startsWith('image-')) {
			// 			const volumeAsM2 = v.viewerInfos.visiblePhysicalImageWidth * v.viewerInfos.visiblePhysicalImageHeight;
			// 			return ([ volumeAsM2, finalMethod ]);
			// 		} else {
			// 			return ([ patchSizeX * patchSizeY, finalMethod ]);
			// 		}
			// 	})();
			// 	const data : Emersya.Basket.IOptionImageCustomizationDescriptor = {
			// 		type :                 'image',
			// 		id :                   this.component_configuration.basketInfos.id,
			// 		configurableMaterial : material.name,
			// 		materialVariation :    material.configuration.enabled,
			// 		sourceFileURL :        v.image.src,
			// 		sourceFileType :       'dataURL',
			// 		productionFileURL :    v.image.src,
			// 		productionFileType :   'dataURL',
			// 		displayFileURL :       v.image.src,
			// 		rotation :             v.rotate,
			// 		position :             <[ number, number ]> [ v.leftRight, v.upDown ],
			// 		scaling :              <[ number, number ]> [ v.resize, v.resize ],
			// 		crop :                 <[ number, number ]> [
			// 			v.viewerInfos.visiblePhysicalImageWidth,
			// 			v.viewerInfos.visiblePhysicalImageHeight
			// 		],
			// 		taxes :                this.component_configuration.basketInfos.taxes,
			// 		price :                this.component_configuration.basketInfos.price,
			// 		priceComputationByVolumeMethod,
			// 		volume
			// 	};
			// 	void data;
			// 	// await B.addOrUpdateCustom(
			// 	// 	this.component_configuration.basketInfos.product,
			// 	// 	this.component_configuration.basketInfos.part,
			// 	// 	data
			// 	// );
			// }
		}
	};

	private async handle_configuration_change(
		event : STD.events.Event<any, CM.Component<any, any>>,
		value : IViewerGraphicsValue
	)
	{
		void event;
		await this.refreshViewerInfosAndBasket();
		const v = <IViewerGraphicsValueWithImage> value;
		if (!value.image || this.component_isDisabled || app.isReloading) {
			return;
		}
		// if (this.configuration.transformMeshOnConfigurationChange !== false) {
		await this.viewer.transformMeshInstancesTextures([
			v.viewerInfos.transformMeshInstanceTexture
		]);
		// }
	};

	async refreshTransformations(ev? : STD.events.Event<any, CM.Component<any, any>>) {
		if (!this.value) {
			throw STD.errors.user('');
		}
		ev = ev || this.component_getEventWhichRepresentNoEvent();
		await this.handle_configuration_change(
			ev,
			this.value
		);
	};

	async refresh(ev? : STD.events.Event<any, CM.Component<any, any>>) {
		if (!this.value) {
			throw STD.errors.user('');
		}
		if (this.component_isDisabled) {
			return;
		}
		ev = ev || this.component_getEventWhichRepresentNoEvent();
		await this.handle_activation_change(
			ev,
			this.value.enabled,
			await this.getNodeLocalId.execute(this, this.component_getEventWhichRepresentNoEvent())
			// ,
			// true
		);
	};

	async getMinX(ev : STD.events.Event<any, CM.Component<any, any>>) {
		void ev;
		const sizeX = await this.getSizeX.execute(this, this.component_getEventWhichRepresentNoEvent());
		return (-(sizeX / 2));
	};

	async getMaxX(ev : STD.events.Event<any, CM.Component<any, any>>) {
		void ev;
		const sizeX = await this.getSizeX.execute(this, this.component_getEventWhichRepresentNoEvent());
		return (sizeX / 2);
	};

	async getMinY(ev : STD.events.Event<any, CM.Component<any, any>>) {
		void ev;
		const sizeY = await this.getSizeY.execute(this, this.component_getEventWhichRepresentNoEvent());
		return (-(sizeY / 2));
	};

	async getMaxY(ev : STD.events.Event<any, CM.Component<any, any>>) {
		void ev;
		const sizeY = await this.getSizeY.execute(this, this.component_getEventWhichRepresentNoEvent());
		return (sizeY / 2);
	};

	cloneValue() {
		if (!this.value) {
			return (null);
		}
		const value = Object.assign(
			{},
			this.value,
			{
				image : this.value.image ? this.value.image.cloneNode(true) : null
			}
		);
		return (value);
	};

	override async component_onActivationChange(ev : STD.events.Event<any, CM.Component<any, any>>) {
		if (app.isReloading) {
			return;
		}
		if (this.value && this.value.enabled) {
			if (!this.component_isDisabled) {
				await this.refresh(ev);
			} else if ((ev.subType === 'disabled') && (this.component_configuration.isPatchNeedingToBeRestoredToBlankOnDisable)) {
				// if (this.component_configuration.basketInfos) {
				// 	const B = <Emersya.Basket.Basket> (<any> window).app.application.basket;
				// 	await B.removeCustomIfUsed(
				// 		this.component_configuration.basketInfos.product,
				// 		this.component_configuration.basketInfos.part,
				// 		'image',
				// 		this.component_configuration.basketInfos.id,
				// 		false
				// 	);
				// }
				await this.viewerApplyImage(null);
				const localId = await this.getNodeLocalId.execute(this, this.component_getEventWhichRepresentNoEvent());
				const material = await this.getMaterial();
				await this.viewer.setMaterials({
					nodeLocalIds : [ localId ],
					materials :    [
						{
							configurableMaterial : material.name,
							materialVariation :    material.configuration.disabled
						}
					]
				});
			}
		}
	};

	async computeBatchFromSupportUpdate() : Promise<ViewerComponent.IBatchDescriptor> {
		await this.refreshViewerInfosAndBasket();
		if (!this.value || !this.value.image || app.isReloading) {
			return ([]);
		}
		return ([
			[ 'transformMeshInstancesTextures', [ this.value.viewerInfos.transformMeshInstanceTexture ] ]
		]);
	};

	async computeBatchFromInsert() : Promise<ViewerComponent.IBatchDescriptor> {
		if (!this.value || !this.value.image || this.component_isDisabled || app.isReloading) {
			return ([]);
		}
		await this.refreshViewerInfosAndBasket();
		const localId = await this.getNodeLocalId.execute(this, this.component_getEventWhichRepresentNoEvent());
		const material = await this.getMaterial();
		const variation = material.configuration[ this.value.enabled ? 'enabled' : 'disabled' ];
		const batch : ViewerComponent.IBatchDescriptor = ([
			[
				'setMaterials',
				{
					nodeLocalIds : [ localId ],
					materials :    [
						{
							configurableMaterial : material.name,
							materialVariation :    variation
						}
					]
				}
			],
			[
				'updateMeshInstanceCustomTextures',
				[
					{
						name :        await this.getMesh(),
						nodeLocalId : localId,
						image :       this.value ? this.value.image.src : STD.colors.transparentBase64PNGPixel,
						contents :    [
							'albedo', 'opacity'
						]
					}
				]
			],
			[ 'transformMeshInstancesTextures', [ this.value.viewerInfos.transformMeshInstanceTexture ] ]
		]);
		return (batch);
	};

	async updateFromSupportUpdate() {
		if (app.isReloading) {
			return;
		}
		await this.viewer.batch(await this.computeBatchFromSupportUpdate());
	};

	private sizeXCache : number | null = null;
	private sizeYCache : number | null = null;

	async reset() {
		await this.updateValue(ViewerGraphicsComponent.getDefaultValue());
		await this.refreshViewerInfosAndBasket();
	};

	override async serialize() {
		if (this.value === null) {
			return (null);
		}
		const out = <IViewerGraphicsValueSerialized> {
			...this.value,
			image : (
								!this.value.image ? null :
									await (((FR, blob) => new Promise((resolve, reject) => {
										FR.onloadend = () => resolve(<string> FR.result);
										FR.onerror = reject;
										FR.readAsDataURL(blob);
									}))(
										new FileReader(),
										await (await fetch(this.value.image.src)).blob()
									))
							)
		};
		return (out);
	};

	private async recomputeMoveBounding(event : STD.events.Event<any, CM.Component<any, any>>) {
		if (!this.component_isStarted || this.component_isDisabled) {
			return;
		}
		if (this.sizeXCache !== null) {
			const sizeX = await this.getSizeX.execute(this, this.component_getEventWhichRepresentNoEvent());
			if (this.sizeXCache === sizeX) {
				return;
			}
			this.sizeXCache = sizeX;
		}
		await this.recomputeMoveLeftRightBounding(event, true);
		await this.recomputeMoveUpDownBounding(event, true);
	};

	private async recomputeMoveLeftRightBounding(
		event : STD.events.Event<any, CM.Component<any, any>>,
		doNotCheckCache = false
	)
	{
		if (!this.component_isStarted || this.component_isDisabled) {
			return;
		}
		let isSameX = false;
		let isSameY = false;
		if ((doNotCheckCache !== true) && (this.sizeXCache !== null)) {
			const sizeX = await this.getSizeX.execute(this, this.component_getEventWhichRepresentNoEvent());
			if (this.sizeXCache === sizeX) {
				isSameX = true;
			}
			this.sizeXCache = sizeX;
		}
		if ((doNotCheckCache !== true) && (this.sizeYCache !== null)) {
			const sizeY = await this.getSizeY.execute(this, this.component_getEventWhichRepresentNoEvent());
			if (this.sizeYCache === sizeY) {
				isSameY = true;
			}
			this.sizeYCache = sizeY;
		}
		if (isSameX && isSameY) {
			return;
		}
		const rc = <InputNumber.Component> this.component_find(CM.Path.parse('@:editor.move-leftRight.input'));
		await rc.recomputeBounds(event);
		if (app.isReloading) {
			return;
		}
		if (this.value && this.value.enabled) {
			const localId = await this.getNodeLocalId.execute(this, this.component_getEventWhichRepresentNoEvent());
			if (typeof localId !== 'number') {
				throw STD.errors.user('getNodeLocalId not returned a number!', {
					localId,
					component : this
				});
			}
			if (!this.viewer.isLoaded()) {
				await this.viewer.awaitLoading();
			}
			await this.handle_configuration_change(event, this.value);
			await this.refresh(event);
		}
	};

	private async recomputeMoveUpDownBounding(
		event : STD.events.Event<any, CM.Component<any, any>>,
		doNotCheckCache = false
	)
	{
		if ((doNotCheckCache !== true) && (this.sizeYCache !== null)) {
			const sizeY = await this.getSizeY.execute(this, this.component_getEventWhichRepresentNoEvent());
			if (this.sizeYCache === sizeY) {
				return;
			}
			this.sizeYCache = sizeY;
		}
		const rc = <InputNumber.Component> this.component_find(CM.Path.parse('@:editor.move-upDown.input'));
		await rc.recomputeBounds(event);
		if (app.isReloading) {
			return;
		}
		if (this.value && this.value.enabled) {
			const localId = await this.getNodeLocalId.execute(this, this.component_getEventWhichRepresentNoEvent());
			if (typeof localId !== 'number') {
				throw STD.errors.user('getNodeLocalId not returned a number!', {
					localId,
					component : this
				});
			}
			if (!this.viewer.isLoaded()) {
				await this.viewer.awaitLoading();
			}
			await this.handle_configuration_change(event, this.value);
			await this.refresh(event);
		}
	};

	constructor(
		descriptor : ConstructorParameters<typeof CM.Component>[0],
		configuration : IViewerGraphicsComponentConfiguration
	)
	{
		if (configuration.forceBackgroundColor) {
			configuration[ '@innerConfigurations' ] = {
				editor : {
					'@innerConfigurations' : {
						image : <InputImage.Configuration> {
							forceBackgroundColor : configuration.forceBackgroundColor
						}
					}
				}
			};
		}
		super(descriptor, configuration);
		if (!configuration.viewer) {
			this.component_throwUserError('Viewer component path not given!', {
				configuration
			});
		}
		this.component_DOM = <HTMLDivElement> STD.DOM.factory({});
		this.getNodeLocalId = CM.PropertyOperation.create(
			configuration.productNode.getNodeLocalId
		);
		this.getSizeX = CM.PropertyOperation.create(
			configuration.productNode.getSizeX
		);
		this.getSizeY = CM.PropertyOperation.create(
			configuration.productNode.getSizeY
		);
		if (configuration.productNode.getMesh) {
			this.getMeshVG = CM.PropertyOperation.create(
				configuration.productNode.getMesh
			);
		}
		if (configuration.productNode.getMaterial) {
			this.getMaterialVG = CM.PropertyOperation.create(
				configuration.productNode.getMaterial
			);
		}
		let getSizeXComponent : CM.Component<any, any> | null = null;
		let getSizeYComponent : CM.Component<any, any> | null = null;
		if (this.getSizeX instanceof CM.PropertyOperation.ReadOperation) {
			getSizeXComponent = this.getSizeX.getComponent(this) || null;
			if (!getSizeXComponent || !getSizeXComponent.component_hasValue()) {
				getSizeXComponent = null;
			}
		}
		if (this.getSizeY instanceof CM.PropertyOperation.ReadOperation) {
			getSizeYComponent = this.getSizeY.getComponent(this) || null;
			if (!getSizeYComponent || !getSizeYComponent.component_hasValue()) {
				getSizeYComponent = null;
			}
		}
		if (getSizeXComponent || getSizeYComponent) {
			if (getSizeXComponent === getSizeYComponent) {
				//no it cant be null!!!!
				this.sizeXCache = NaN;
				this.sizeYCache = NaN;
				(<CM.Component<any, any>> getSizeXComponent).addEventListener(
					'value',
					this.recomputeMoveLeftRightBounding.bind(this),
					{
						listener : this,
						subType :  (<CM.Component<any, any>> getSizeXComponent).static.type
					}
				);
			} else {
				if (getSizeXComponent) {
					this.sizeXCache = NaN;
					getSizeXComponent.addEventListener('value', this.recomputeMoveBounding.bind(this), {
						listener : this,
						subType :  getSizeXComponent.static.type
					});
				}
				if (getSizeYComponent) {
					this.sizeYCache = NaN;
					getSizeYComponent.addEventListener('value', this.recomputeMoveUpDownBounding.bind(this), {
						listener : this,
						subType :  getSizeYComponent.static.type
					});
				}
			}
		}
		this.getProductInstance = CM.PropertyOperation.create(
			configuration.productNode.getProductInstance
		);
	};
};
export namespace NS {
	export const Component = ViewerGraphicsComponent;
	export type Component = ViewerGraphicsComponent;
	export type Configuration = IViewerGraphicsComponentConfiguration;
	export type ValueSerialized = IViewerGraphicsValueSerialized;
}
import manifestXMLSource                 from './ViewerGraphics.xml';
import './ViewerGraphics.scss';

CM.declare({
	id :       'viewer-graphics',
	type :     ViewerGraphicsComponent,
	manifest : manifestXMLSource
});
import ViewerComponent                   from '../Viewer/Viewer';
import { MPAEngineApplicationComponent } from '../../mpa-engine/Application/MPAEngineApplication';
